/**
 * ToastOptions stores configuration for Call-to-Action text shown next to the
 * placeholder avatar.
 */
export interface ToastOptions {
  message: string;
  placeholderTimeout: number;
}

/**
 * AvatarOptions holds configuration for the placeholder avatar.
 */
export interface AvatarOptions {
  toast?: ToastOptions;
}

/**
 * Bot connection options.
 */
export interface BotOptions {
  moonkin: {
    url: string;
    username: string;
    password: string;
  };
  group: string;
}

export enum ChatbotType {
  WEBSITE = 'website',
  AVATAR = 'avatar',
  PHONEBOT = 'phonebot',
}

export interface ChatColorScheme {
  'mat-primary-500': string;
  'mat-primary-contrast-500': string;
  'mat-secondary-500': string;
  'mat-secondary-contrast-500': string;
  'chatbot-background-color': string;
}

export enum FloatingIconStyle {
  STATIC_IMAGE = 'static-image',
  DYNAMIC_AVATAR = 'dynamic-avatar',
}

export enum AvatarDisplayMode {
  ABOVE_CHAT_LANDSCAPE = 'above-chat-landscape',
  BACKGROUND_PORTRAIT = 'background-portrait',
}

export enum ClickOptionsStyle {
  FILLED = 'filled',
  FRAMED = 'framed',
}

export enum TtsProvider {
  AZURE_AI_SPEECH = 'azure',
  ELEVENLABS = 'eleven-labs',
}

export interface PhonebotPronunciationRule {
  term: string;
  languageLocale: string;
}

export interface PhonebotChatConfig {
  tts: {
    provider: TtsProvider;
    voice: string;
    speechSpeed: number;
  };

  pronunciationRules: PhonebotPronunciationRule[];
  customTerminology: string[];
}

export interface WebsiteChatConfig {
  dynamicAvatarEnabled: boolean;
  floatingIconStyle: FloatingIconStyle;
  avatarDisplayMode: AvatarDisplayMode;
  colorScheme: ChatColorScheme;
  clickOptionsStyle: ClickOptionsStyle;
  placeholderGreetingDuration: number;
  showGreetingOnce: boolean;
  keepChatOpen: boolean;
}

export interface AvatarChatConfig {
  slogan?: string;
  fallbackImageLink?: string;
  companyLogoLink?: string;
  introVideoEnabled: boolean;
  introVideoLink?: string;
  avatarIdleDelaySec: number;
  colorScheme: ChatColorScheme;
  introVideoButtonText: string;
  clickOptionsStyle: ClickOptionsStyle;
  hideTextMessages: boolean;
}

/**
 * Config data from the server.
 */
export interface CustomConfig {
  loopVideoLink?: string;
  kauzChatUserstyleURL: string;
  botAvatar: string;
  placeholderFloatingIcon?: string;
  kauzChatCorsOrigin: string;
  customStyles: string;
  type: ChatbotType;
  websiteChatConfig: WebsiteChatConfig;
  avatarChatConfig: AvatarChatConfig;
  phonebotChatConfig: PhonebotChatConfig;
}

export interface Configurations {
  placeholder: {
    avatar: AvatarOptions;
  };
  userStyle: {
    url: string;
  };
  bot: BotOptions;
  type: ChatbotType;
  colorScheme: ChatColorScheme;
  loopVideoLink?: string;
  websiteChatConfig: WebsiteChatConfig;
  avatarChatConfig: AvatarChatConfig;
  phonebotChatConfig: PhonebotChatConfig;
  botAvatar: string;
  placeholderFloatingIcon?: string;
  multiClientSupport: boolean;
  modules: {
    i18n: {
      translations: ChatTranslations[];
    };
  };
  supportedHosts: string[];
  customStylesExpanded: string;
}

export declare interface MetaEntry {
  key: string;
  value: string;
}

export interface ChatTranslations {
  id: number;
  group: string;
  languageCode: string;
  kauzChatGreeting: string;
  kauzChatHeaderMessage: string;
  kauzChatInputPlaceholder: string;
  kauzChatInfoboxContent: string;
  isDefault: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface DefaultLanguage {
  code: string;
}
